import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Radio, Video, ExternalLink, Users, Calendar, Clock, Eye } from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export default function StreamerPage() {
  const { streamerId } = useParams();
  const [streamer, setStreamer] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStreamer();
  }, [streamerId]);

  const fetchStreamer = async () => {
    try {
      const [streamerRes, interviewsRes] = await Promise.all([
        fetch(`${API}/streamers/${streamerId}`),
        fetch(`${API}/interviews?host_id=${streamerId}`)
      ]);
      
      if (streamerRes.ok) {
        setStreamer(await streamerRes.json());
      } else {
        setError('Streamer not found');
      }
      
      if (interviewsRes.ok) {
        setInterviews(await interviewsRes.json());
      }
    } catch (err) {
      setError('Failed to load streamer');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !streamer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="glass-card p-8 text-center max-w-md">
          <Radio className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-2xl font-bold mb-2">Streamer Not Found</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/creatives">
            <Button>Back to Creatives</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="streamer-page">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="relative mb-8">
          <div className="h-48 sm:h-64 rounded-xl bg-gradient-to-br from-red-900/50 to-orange-900/50 overflow-hidden">
            {streamer.banner_url && (
              <img src={streamer.banner_url} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 -mt-16 sm:-mt-20 px-4 sm:px-6">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-red-500/20 border-4 border-background flex items-center justify-center overflow-hidden">
              {streamer.avatar_url ? (
                <img src={streamer.avatar_url} alt={streamer.display_name} className="w-full h-full object-cover" />
              ) : (
                <Radio className="w-16 h-16 text-red-400" />
              )}
            </div>
            <div className="flex-1 pt-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="streamer-name">
                  {streamer.display_name}
                </h1>
                {streamer.is_verified && (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Verified</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                <Badge variant="outline">{streamer.stream_type}</Badge>
                <span className="flex items-center gap-1">
                  <Video className="w-4 h-4" /> {streamer.interviews_count || 0} interviews
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {streamer.subscribers_count || 0} subscribers
                </span>
              </div>
              {streamer.booking_link && (
                <a href={streamer.booking_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-red-600 hover:bg-red-700">
                    <Calendar className="w-4 h-4 mr-2" /> Book for Stream
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs defaultValue="interviews">
              <TabsList className="bg-muted/50 mb-6">
                <TabsTrigger value="interviews">Interviews</TabsTrigger>
                <TabsTrigger value="about">About</TabsTrigger>
              </TabsList>

              <TabsContent value="interviews">
                {interviews.length > 0 ? (
                  <div className="space-y-4">
                    {interviews.map((interview) => (
                      <Link key={interview.interview_id} to={`/interviews/${interview.interview_id}`}>
                        <Card className="glass-card border-border hover:border-border transition-all p-4">
                          <div className="flex gap-4">
                            <div className="w-32 h-20 rounded bg-muted/30 flex items-center justify-center flex-shrink-0">
                              {interview.cover_url ? (
                                <img src={interview.cover_url} alt="" className="w-full h-full object-cover rounded" />
                              ) : (
                                <Video className="w-8 h-8 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold line-clamp-1">{interview.title}</h3>
                              <p className="text-sm text-muted-foreground">Guest: {interview.guest_name}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" /> {interview.plays || 0}
                                </span>
                                {interview.duration && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {interview.duration}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Card className="glass-card border-border p-8 text-center">
                    <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">No interviews yet</p>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="about">
                <Card className="glass-card border-border p-6">
                  <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Bio</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {streamer.bio || 'No bio provided yet.'}
                  </p>
                  
                  {streamer.platforms && streamer.platforms.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Platforms</h3>
                      <div className="flex flex-wrap gap-2">
                        {streamer.platforms.map((platform, i) => (
                          <Badge key={i} variant="outline">{platform}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-6">
            <Card className="glass-card border-border p-4">
              <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Interviews</span>
                  <span className="font-bold">{streamer.interviews_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Streams</span>
                  <span className="font-bold">{streamer.streams_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subscribers</span>
                  <span className="font-bold">{streamer.subscribers_count || 0}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
